import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import StandardTable from "../../../ui/components/StandardTable";
import Pagination from "../../../components/utils/Pagination";
import EmptyState from "../../../components/EmptyState";
import { InlineLoader } from "../../../components/loader/CharacterizationLoader";
import {
  getContainerNotificationDeliveries,
  getContainerNotificationUsers,
} from "../../../services/notification";

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
};

const formatUserName = (user) => {
  if (!user) return "—";
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  return name || user.email || "—";
};

const statusToneClass = (severity) => {
  if (severity === "ok") return "text-[#1B7A4E]";
  if (severity === "ko") return "text-[#AF2F3C]";
  if (severity === "warn") return "text-[#B54708]";
  return "text-[#5e5e5e]";
};

const deliveryStatusClass = (status) => {
  if (status === "opened" || status === "delivered") return "text-[#1B7A4E]";
  if (status === "failed") return "text-[#AF2F3C]";
  if (status === "accepted") return "text-[#B54708]";
  return "text-[#5e5e5e]";
};

const DetailItem = ({ label, value }) => (
  <div className="min-w-[140px]">
    <div className="text-[11px] uppercase tracking-wide text-[#828899]">
      {label}
    </div>
    <div className="text-[13px] text-[#222222] mt-0.5">{value}</div>
  </div>
);

const DeliveryTimeline = ({ row, t }) => {
  const steps = [
    {
      key: "accepted",
      label: t("containerNotifications.timeline.accepted"),
      at: row.acceptedAt,
      done:
        Boolean(row.acceptedAt) ||
        ["accepted", "delivered", "opened"].includes(row.status),
      failed: false,
    },
    {
      key: "delivered",
      label: t("containerNotifications.timeline.delivered"),
      at: row.deliveredAt,
      done: Boolean(row.deliveredAt) || row.status === "opened",
      failed: false,
    },
    {
      key: "opened",
      label: t("containerNotifications.timeline.opened"),
      at: row.openedAt,
      done: Boolean(row.openedAt) || row.status === "opened",
      failed: false,
    },
  ];

  if (row.status === "failed") {
    steps[0] = {
      key: "failed",
      label: t("containerNotifications.timeline.failed"),
      at: row.acceptedAt || row.date,
      done: true,
      failed: true,
    };
  }

  return (
    <div className="flex flex-wrap gap-3 mt-2">
      {steps.map((step) => (
        <div
          key={step.key}
          className={`rounded px-2 py-1 border text-[12px] ${
            step.failed
              ? "border-[#f0c4c8] bg-[#fdf5f6] text-[#AF2F3C]"
              : step.done
                ? "border-[#b7e1c8] bg-[#f3fbf6] text-[#1B7A4E]"
                : "border-[#e5e5e5] bg-[#fafafa] text-[#828899]"
          }`}
        >
          <div className="font-medium">{step.label}</div>
          <div>{formatDateTime(step.at)}</div>
        </div>
      ))}
    </div>
  );
};

const OptInChips = ({ user, t }) => {
  const chips = [
    {
      on: user.receiveAnomalyPush,
      label: t("containerNotifications.optIn.anomaly"),
    },
    {
      on: user.receiveFillingPush,
      label: t("containerNotifications.optIn.filling"),
    },
    {
      on: user.recieveCharacterizationPush,
      label: t("containerNotifications.optIn.characterization"),
    },
    {
      on: user.recieveQualicheckPush,
      label: t("containerNotifications.optIn.qualicheck"),
    },
    {
      on: user.receiveRotationPush,
      label: t("containerNotifications.optIn.rotation"),
    },
  ];

  return (
    <div className="flex flex-wrap gap-1.5 mt-1">
      {chips.map((chip) => (
        <span
          key={chip.label}
          className={`text-[11px] px-2 py-0.5 rounded ${
            chip.on
              ? "bg-[#eaf7f0] text-[#1B7A4E]"
              : "bg-[#f2f2f2] text-[#828899]"
          }`}
        >
          {chip.label}
        </span>
      ))}
    </div>
  );
};

const ContainerViewNotifications = ({ containerId }) => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState(false);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [expandedDeliveryId, setExpandedDeliveryId] = useState(null);

  const [deliveries, setDeliveries] = useState([]);
  const [deliveriesCount, setDeliveriesCount] = useState(0);
  const [deliveriesLoading, setDeliveriesLoading] = useState(true);
  const [deliveriesError, setDeliveriesError] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const selectedUser = useMemo(
    () => users.find((user) => String(user._id) === String(selectedUserId)),
    [users, selectedUserId],
  );

  useEffect(() => {
    if (!containerId) return undefined;
    let cancelled = false;

    const loadUsers = async () => {
      setUsersLoading(true);
      setUsersError(false);
      try {
        const response = await getContainerNotificationUsers(containerId);
        if (cancelled) return;
        setUsers(response.data?.users || []);
        setSummary(response.data?.summary || null);
      } catch {
        if (!cancelled) {
          setUsers([]);
          setSummary(null);
          setUsersError(true);
        }
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    };

    loadUsers();
    return () => {
      cancelled = true;
    };
  }, [containerId]);

  useEffect(() => {
    if (!containerId) return undefined;
    let cancelled = false;

    const loadDeliveries = async () => {
      setDeliveriesLoading(true);
      setDeliveriesError(false);
      try {
        const response = await getContainerNotificationDeliveries(containerId, {
          page,
          limit,
          status: statusFilter,
          type: typeFilter,
          userId: selectedUserId || undefined,
        });
        if (cancelled) return;
        setDeliveries(response.data?.data || []);
        setDeliveriesCount(response.data?.count || 0);
      } catch {
        if (!cancelled) {
          setDeliveries([]);
          setDeliveriesCount(0);
          setDeliveriesError(true);
        }
      } finally {
        if (!cancelled) setDeliveriesLoading(false);
      }
    };

    loadDeliveries();
    return () => {
      cancelled = true;
    };
  }, [containerId, page, limit, statusFilter, typeFilter, selectedUserId]);

  const typeOptions = useMemo(
    () => [
      { value: "all", label: t("All") },
      {
        value: "filling_level",
        label: t("containerNotifications.type.filling_level"),
      },
      {
        value: "sorting_error",
        label: t("containerNotifications.type.sorting_error"),
      },
      {
        value: "sorting_campaign",
        label: t("containerNotifications.type.sorting_campaign"),
      },
      {
        value: "characterization",
        label: t("containerNotifications.type.characterization"),
      },
      {
        value: "qualicheck",
        label: t("containerNotifications.type.qualicheck"),
      },
      { value: "push_test", label: t("containerNotifications.type.push_test") },
    ],
    [t],
  );

  const statusOptions = useMemo(
    () => [
      { value: "all", label: t("All") },
      {
        value: "accepted",
        label: t("containerNotifications.status.accepted"),
      },
      {
        value: "delivered",
        label: t("containerNotifications.status.delivered"),
      },
      { value: "opened", label: t("containerNotifications.status.opened") },
      { value: "failed", label: t("containerNotifications.status.failed") },
    ],
    [t],
  );

  const toggleExpandUser = (userId) => {
    setExpandedUserId((current) =>
      String(current) === String(userId) ? null : userId,
    );
  };

  const filterHistoryByUser = (userId) => {
    const next =
      selectedUserId && String(selectedUserId) === String(userId)
        ? null
        : userId;
    setSelectedUserId(next);
    setPage(1);
    setExpandedDeliveryId(null);
  };

  return (
    <div className="px-4 md:px-6 pb-8 space-y-8">
      <section>
        <div className="mb-3">
          <h3 className="text-[16px] font-semibold text-[#222222] mb-1">
            {t("containerNotifications.usersTitle")}
          </h3>
          <p className="text-[13px] text-[#828899] mb-0">
            {t("containerNotifications.usersHelpDetailed")}
          </p>
          {summary ? (
            <p className="text-[13px] text-[#5e5e5e] mt-2 mb-0">
              {t("containerNotifications.usersSummary", {
                total: summary.totalUsers,
                optIn: summary.optInCount,
                ok: summary.subscribedCount,
                atRisk: summary.atRiskCount,
              })}
            </p>
          ) : null}
        </div>

        {usersLoading ? (
          <InlineLoader height="140px" />
        ) : usersError ? (
          <EmptyState title={t("containerNotifications.loadError")} />
        ) : users.length === 0 ? (
          <EmptyState title={t("containerNotifications.usersEmpty")} />
        ) : (
          <StandardTable
            fluidColCount={4}
            fixedColWidth={0}
            columnAlignments={["left", "left", "left", "left"]}
            topBar={null}
            header={
              <div className="table-standard-header">
                <div className="table-standard-header-cell">
                  {t("containerNotifications.colUser")}
                </div>
                <div className="table-standard-header-cell">
                  {t("containerNotifications.colOptIn")}
                </div>
                <div className="table-standard-header-cell">
                  {t("containerNotifications.colStatus")}
                </div>
                <div className="table-standard-header-cell">
                  {t("containerNotifications.colLastForContainer")}
                </div>
              </div>
            }
          >
            {users.map((user) => {
              const isExpanded = String(expandedUserId) === String(user._id);
              const isFiltered = String(selectedUserId) === String(user._id);
              const diagnostics = user.pushDiagnostics || {};
              return (
                <div key={user._id}>
                  <div
                    className={`table-standard-row cursor-pointer ${
                      isFiltered ? "bg-[#f3fbf6]" : ""
                    }`}
                    onClick={() => toggleExpandUser(user._id)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        toggleExpandUser(user._id);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="table-standard-row-cell">
                      <div className="flex items-start gap-2 min-w-0">
                        <span className="mt-1 text-[#828899]" aria-hidden>
                          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </span>
                        <div className="flex flex-col min-w-0">
                          <span className="font-medium text-[#222222] truncate">
                            {formatUserName(user)}
                          </span>
                          <span className="text-[12px] text-[#828899] truncate">
                            {user.email || "—"}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="table-standard-row-cell">
                      <div className="flex flex-col gap-0.5 min-w-0 py-0.5">
                        <span className="font-medium text-[#222222]">
                          {user.optIn
                            ? t("settingsUsers.pushYes")
                            : t("settingsUsers.pushNo")}
                        </span>
                        <span className="text-[12px] text-[#828899]">
                          {t("containerNotifications.tokensCount", {
                            count: user.fcmTokenCount || 0,
                          })}
                        </span>
                      </div>
                    </div>
                    <div className="table-standard-row-cell">
                      <div className="flex flex-col items-start gap-1.5 min-w-0 py-0.5">
                        <span
                          className={`font-medium leading-snug ${statusToneClass(
                            user.diagnosis?.severity,
                          )}`}
                        >
                          {t(
                            `settingsUsers.pushDiagnosis.${user.diagnosis?.code || "unknown"}.title`,
                            {
                              defaultValue: user.diagnosis?.code || "unknown",
                            },
                          )}
                        </span>
                        {user.pushAtRisk ? (
                          <span
                            className="shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded bg-[#fff1e8] text-[#B54708]"
                            title={t("settingsUsers.pushAtRiskBadgeHint")}
                          >
                            {t("settingsUsers.pushAtRiskBadge")}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className="table-standard-row-cell">
                      {user.lastDeliveryForContainer ? (
                        <div className="flex flex-col gap-0.5 min-w-0 py-0.5">
                          <span
                            className={deliveryStatusClass(
                              user.lastDeliveryForContainer.status,
                            )}
                          >
                            {t(
                              `containerNotifications.status.${user.lastDeliveryForContainer.status}`,
                              {
                                defaultValue:
                                  user.lastDeliveryForContainer.status,
                              },
                            )}
                          </span>
                          <span className="text-[12px] text-[#828899]">
                            {formatDateTime(
                              user.lastDeliveryForContainer.openedAt ||
                                user.lastDeliveryForContainer.deliveredAt ||
                                user.lastDeliveryForContainer.acceptedAt ||
                                user.lastDeliveryForContainer.date,
                            )}
                          </span>
                        </div>
                      ) : (
                        <span className="text-[#828899]">
                          {t("containerNotifications.neverForContainer")}
                        </span>
                      )}
                    </div>
                  </div>

                  {isExpanded ? (
                    <div className="border-b border-[#ececec] bg-[#fafafa] px-4 py-3">
                      <div className="flex flex-wrap gap-4 mb-3">
                        <DetailItem
                          label={t("settingsUsers.pushBrowserPermission")}
                          value={t(
                            `settingsUsers.pushPermission.${diagnostics.permission || "unknown"}`,
                            {
                              defaultValue: diagnostics.permission || "unknown",
                            },
                          )}
                        />
                        <DetailItem
                          label={t("settingsUsers.pushPlatform")}
                          value={t(
                            `settingsUsers.pushPlatformValue.${diagnostics.platform || "unknown"}`,
                            {
                              defaultValue: diagnostics.platform || "unknown",
                            },
                          )}
                        />
                        <DetailItem
                          label={t("settingsUsers.pushStandalone")}
                          value={
                            diagnostics.isStandalone === true
                              ? t("settingsUsers.pushYes")
                              : diagnostics.isStandalone === false
                                ? t("settingsUsers.pushNo")
                                : t("settingsUsers.pushUnknown")
                          }
                        />
                        <DetailItem
                          label={t("settingsUsers.pushLastSeen")}
                          value={formatDateTime(diagnostics.lastSeenAt)}
                        />
                        <DetailItem
                          label={t("settingsUsers.pushLastError")}
                          value={
                            diagnostics.lastPushError ||
                            t("settingsUsers.pushNone")
                          }
                        />
                      </div>

                      <div className="mb-3">
                        <div className="text-[11px] uppercase tracking-wide text-[#828899] mb-1">
                          {t("containerNotifications.optInsDetail")}
                        </div>
                        <OptInChips user={user} t={t} />
                      </div>

                      <p className="text-[13px] text-[#5e5e5e] mb-3">
                        {t(
                          `settingsUsers.pushDiagnosis.${user.diagnosis?.code || "unknown"}.action`,
                          { defaultValue: "" },
                        )}
                      </p>

                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="char-button char-button-secondary"
                          onClick={(event) => {
                            event.stopPropagation();
                            filterHistoryByUser(user._id);
                          }}
                        >
                          {isFiltered
                            ? t("containerNotifications.clearUserFilter")
                            : t("containerNotifications.filterHistoryByUser")}
                        </button>
                        <Link
                          to={`/access/${user._id}`}
                          className="char-button char-button-secondary no-underline"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {t("containerNotifications.openUserProfile")}
                        </Link>
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </StandardTable>
        )}
      </section>

      <section>
        <div className="mb-3 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <h3 className="text-[16px] font-semibold text-[#222222] mb-1">
              {t("containerNotifications.historyTitle")}
            </h3>
            <p className="text-[13px] text-[#828899] mb-0">
              {t("containerNotifications.historyHelpDetailed")}
            </p>
            {selectedUser ? (
              <div className="mt-2 inline-flex items-center gap-2 rounded bg-[#eaf7f0] text-[#1B7A4E] text-[12px] px-2 py-1">
                <span>
                  {t("containerNotifications.filteredByUser", {
                    name: formatUserName(selectedUser),
                  })}
                </span>
                <button
                  type="button"
                  className="underline"
                  onClick={() => filterHistoryByUser(selectedUser._id)}
                >
                  {t("containerNotifications.clearUserFilter")}
                </button>
              </div>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            <label className="text-[12px] text-[#828899]">
              {t("containerNotifications.filterType")}
              <select
                className="ml-2 border border-[#d3d3d3] rounded px-2 py-1 text-[13px] text-[#222222]"
                value={typeFilter}
                onChange={(event) => {
                  setPage(1);
                  setTypeFilter(event.target.value);
                }}
              >
                {typeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-[12px] text-[#828899]">
              {t("containerNotifications.filterStatus")}
              <select
                className="ml-2 border border-[#d3d3d3] rounded px-2 py-1 text-[13px] text-[#222222]"
                value={statusFilter}
                onChange={(event) => {
                  setPage(1);
                  setStatusFilter(event.target.value);
                }}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {deliveriesLoading ? (
          <InlineLoader height="140px" />
        ) : deliveriesError ? (
          <EmptyState title={t("containerNotifications.loadError")} />
        ) : deliveries.length === 0 ? (
          <EmptyState title={t("containerNotifications.historyEmpty")} />
        ) : (
          <StandardTable
            fluidColCount={5}
            fixedColWidth={0}
            columnAlignments={["left", "left", "left", "left", "left"]}
            topBar={null}
            header={
              <div className="table-standard-header">
                <div className="table-standard-header-cell">
                  {t("containerNotifications.colDate")}
                </div>
                <div className="table-standard-header-cell">
                  {t("containerNotifications.colType")}
                </div>
                <div className="table-standard-header-cell">
                  {t("containerNotifications.colChannel")}
                </div>
                <div className="table-standard-header-cell">
                  {t("containerNotifications.colRecipient")}
                </div>
                <div className="table-standard-header-cell">
                  {t("containerNotifications.colDelivery")}
                </div>
              </div>
            }
            bottom={
              <Pagination
                totalEntry={deliveriesCount}
                recordPerPage={limit}
                setRecordPerPage={(value) => {
                  setPage(1);
                  setLimit(value);
                }}
                currentPage={page}
                setCurrentPage={setPage}
                resultPage={(page - 1) * limit}
                setResultPage={() => {}}
              />
            }
          >
            {deliveries.map((row) => {
              const isExpanded = expandedDeliveryId === row.rowId;
              return (
                <div key={row.rowId}>
                  <div
                    className="table-standard-row cursor-pointer"
                    onClick={() =>
                      setExpandedDeliveryId((current) =>
                        current === row.rowId ? null : row.rowId,
                      )
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setExpandedDeliveryId((current) =>
                          current === row.rowId ? null : row.rowId,
                        );
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="table-standard-row-cell">
                      <div className="flex items-start gap-2">
                        <span className="mt-1 text-[#828899]" aria-hidden>
                          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </span>
                        <span>
                          {formatDateTime(row.acceptedAt || row.date)}
                        </span>
                      </div>
                    </div>
                    <div className="table-standard-row-cell">
                      {t(`containerNotifications.type.${row.type}`, {
                        defaultValue: row.type,
                      })}
                    </div>
                    <div className="table-standard-row-cell">
                      {t("containerNotifications.channel.push")}
                    </div>
                    <div className="table-standard-row-cell">
                      <div className="flex flex-col min-w-0">
                        <span className="truncate">
                          {formatUserName(row.recipient)}
                        </span>
                        <span className="text-[12px] text-[#828899] truncate">
                          {row.recipient?.email || "—"}
                        </span>
                      </div>
                    </div>
                    <div className="table-standard-row-cell">
                      <div className="flex flex-col">
                        <span className={deliveryStatusClass(row.status)}>
                          {t(`containerNotifications.status.${row.status}`, {
                            defaultValue: row.status,
                          })}
                        </span>
                        {row.openedAt ? (
                          <span className="text-[12px] text-[#828899]">
                            {t("containerNotifications.openedAt", {
                              date: formatDateTime(row.openedAt),
                            })}
                          </span>
                        ) : row.deliveredAt ? (
                          <span className="text-[12px] text-[#828899]">
                            {t("containerNotifications.deliveredAt", {
                              date: formatDateTime(row.deliveredAt),
                            })}
                          </span>
                        ) : row.error ? (
                          <span className="text-[12px] text-[#AF2F3C] truncate">
                            {row.error}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  {isExpanded ? (
                    <div className="border-b border-[#ececec] bg-[#fafafa] px-4 py-3">
                      {row.description ? (
                        <p className="text-[13px] text-[#5e5e5e] mb-2">
                          {row.description}
                        </p>
                      ) : null}
                      <DeliveryTimeline row={row} t={t} />
                      <div className="flex flex-wrap gap-4 mt-3">
                        <DetailItem
                          label={t("containerNotifications.colNotificationId")}
                          value={String(row.notificationId || "—")}
                        />
                        <DetailItem
                          label={t("containerNotifications.colFcmMessageId")}
                          value={row.fcmMessageId || "—"}
                        />
                        <DetailItem
                          label={t("settingsUsers.pushLastError")}
                          value={row.error || t("settingsUsers.pushNone")}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </StandardTable>
        )}
      </section>
    </div>
  );
};

export default ContainerViewNotifications;
